import { useRetainedRef } from "@appretio/shared";
import { useState } from "react";
import { forecastRowId } from "../../components/cubeDataTable/utils/rowId";
import {
  type ForecastTableControlValues,
  isEditableCalendarView
} from "../../components/tableControls/forecastTableControlValues";
import type { TemporalGridRow } from "../../utils/weekly/temporalGridAdapter";
import { type CubePage, pageScope } from "../useCubeSelectionState";

type RowPatch = Record<string, TemporalGridRow>;

/** Post-cascade rows shared with the currently mounted workspace. */
export function useRetainedGridRows(page: CubePage) {
  const scope = pageScope(page);
  const patchRef = useRetainedRef<RowPatch>(scope, "gridRowPatch", {});
  const generationRef = useRetainedRef(scope, "loadGeneration", 0);
  const listenersRef = useRetainedRef<Set<() => void>>(
    scope,
    "rowListeners",
    new Set()
  );
  const [rows] = useState(() => ({
    /** Subscribe to transactions from this mount or an earlier one. */
    subscribeRows(listener: () => void) {
      listenersRef.current.add(listener);
      return () => {
        listenersRef.current.delete(listener);
      };
    },
    /** Stable snapshot for useSyncExternalStore. */
    getRowPatch() {
      return patchRef.current;
    },
    /** Capture before awaiting a transaction; replacement data invalidates it. */
    rowSink() {
      const generation = generationRef.current;
      return (updates: TemporalGridRow[]) => {
        if (generation !== generationRef.current || updates.length === 0)
          return;
        const patch = { ...patchRef.current };
        for (const row of updates) patch[forecastRowId(row)] = row;
        patchRef.current = patch;
        listenersRef.current.forEach((listener) => listener());
      };
    },
    /** Gregorian rows share ids but contain different aggregations. */
    patchRows(
      rows: TemporalGridRow[],
      calendarView: ForecastTableControlValues["calendarView"],
      patch = patchRef.current
    ) {
      if (!isEditableCalendarView(calendarView)) return rows;
      return rows.map((row) => patch[forecastRowId(row)] ?? row);
    },
    /** The data replacement renders its own rows; do not interrupt that commit. */
    clearRows() {
      generationRef.current += 1;
      patchRef.current = {};
    }
  }));
  return rows;
}
